import { useState } from "react";
import { BrowserRouter, Routes, Route, Navigate, Outlet } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import { FleetMetrics, FleetMetricsOpenSearch } from "../../pages/fleet_metrics";
import PrivateRoute from "./PrivateRoute";
import Login from "../../pages/Auth/Login";
import EvSummary from "../../pages/charger_details/EvSummary";
import ProtectedRoute from "../components/ProtectedRoute";
import { FEATURES } from "../utils/featureAccess";
import EnphaseStationsRoute from "./EnphaseStationsRoute";
import UserManagement from "../../pages/user_management/UserManagement";
import RoleManagement from "../../pages/user_management/RoleManagement";
import PolicyManagement from "../../pages/user_management/PolicyManagement";
import { FleetDashboard, FleetDashboardOpenSearch } from "../../pages/fleet_dashboard";
import { FleetDataProvider } from "../context/FleetDataProvider";
import FleetSection, { FleetIndexRedirect } from "../../pages/sections/FleetSection";
import ChargerSection from "../../pages/sections/ChargerSection";
import AdminSection from "../../pages/sections/AdminSection";
import StationDashboard from "../../pages/stations/StationDashboard";
import StationList from "../../pages/stations/StationList";
import ManageStation from "../../pages/stations/ManageStation";
import TabDemo from "../../pages/TabDemo";
import Settings from "../../pages/settings/Settings";
import { TimezoneProvider } from "../context/TimezoneProvider";

function AuthenticatedLayout() {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  return (
    <PrivateRoute>
      <FleetDataProvider>
        <TimezoneProvider>
          <div className="app-layout">
            <Sidebar collapsed={sidebarCollapsed} onToggleCollapse={() => setSidebarCollapsed(prev => !prev)} />
            <main className="main-content">
              <Outlet />
            </main>
          </div>
        </TimezoneProvider>
      </FleetDataProvider>
    </PrivateRoute>
  );
}

export default function AppRouter() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route element={<AuthenticatedLayout />}>
          {/* Redirect root to /fleet */}
          <Route path="/" element={<Navigate to="/fleet" replace />} />

          {/* Fleet Section */}
          <Route path="/fleet" element={<FleetSection />}>
            <Route index element={<FleetIndexRedirect />} />
            <Route path="dashboard" element={<FleetDashboardOpenSearch />} />
            <Route
              path="v1"
              element={
                <ProtectedRoute featureId={FEATURES.USER_MANAGEMENT} fallbackPath="/fleet/dashboard">
                  <FleetDashboard />
                </ProtectedRoute>
              }
            />
            <Route path="list" element={<FleetMetricsOpenSearch />} />
            <Route
              path="list/v1"
              element={
                <ProtectedRoute featureId={FEATURES.USER_MANAGEMENT} fallbackPath="/fleet/list">
                  <FleetMetrics />
                </ProtectedRoute>
              }
            />
          </Route>

          {/* Stations Section — hidden for Enphase users */}
          <Route path="/stations" element={<EnphaseStationsRoute />}>
            <Route index element={<StationDashboard />} />
            <Route path="manage" element={<StationList />} />
            <Route path="manage/:stationId" element={<ManageStation />} />
            {/* Legacy redirects */}
            <Route path="create" element={<Navigate to="/stations/manage?mode=create" replace />} />
            <Route path="hubs" element={<Navigate to="/stations/manage" replace />} />
            <Route path="hubs/:hubId" element={<Navigate to="/stations/manage" replace />} />
          </Route>

          {/* Chargers Section */}
          <Route path="/chargers" element={<ChargerSection />}>
            <Route index element={<EvSummary />} />
            <Route path=":chargerId" element={<EvSummary />} />
            <Route path="search" element={<FleetMetrics />} />
          </Route>

          {/* Settings */}
          <Route path="/settings" element={<Settings />} />

          {/* Tab Demo - Development */}
          <Route path="/tab-demo" element={<TabDemo />} />

          {/* Admin Section */}
          <Route
            path="/admin"
            element={
              <ProtectedRoute featureId={FEATURES.USER_MANAGEMENT}>
                <AdminSection />
              </ProtectedRoute>
            }
          >
            <Route index element={<UserManagement />} />
            <Route
              path="roles"
              element={
                <ProtectedRoute featureId={FEATURES.SYSTEM_SETTINGS}>
                  <RoleManagement />
                </ProtectedRoute>
              }
            />
            <Route
              path="policies"
              element={
                <ProtectedRoute featureId={FEATURES.SYSTEM_SETTINGS}>
                  <PolicyManagement />
                </ProtectedRoute>
              }
            />
          </Route>
        </Route>
      </Routes>
    </BrowserRouter>
  );
}
